import { Injectable, signal } from '@angular/core';
import type { AskSource } from '../models/ask.models';

export interface CitationPreviewEntry {
  /** 1-based number as shown in the answer text. */
  readonly rank: number;
  readonly source: AskSource;
}

export interface PreviewPosition {
  top: number;
  left: number;
}

/**
 * Horizontal alignment of the popover on its trigger. It always opens below
 * the trigger (above when there is no room) and stays inside the panel.
 * - `start`: left edges aligned — citation markers in the answer text.
 * - `end`: right edges aligned, so it opens towards the left — the eye button
 *   of the source list, which sits at the panel's right edge.
 */
export type PreviewAlign = 'start' | 'end';

const MARGIN = 8;
const GAP = 6;
/** Below this, shrinking the popover to the free space is not worth it. */
const MIN_HEIGHT = 96;

/**
 * Shared state for the citation-extract popover.
 *
 * The popover is rendered at the top level of the widget's shadow root rather
 * than inside a message: the panel animates with `transform`, and a transformed
 * ancestor would become the containing block of a `position: fixed` descendant,
 * throwing off its coordinates. Keeping it a sibling of the panel avoids that
 * class of bug entirely.
 *
 * It is placed inside the widget panel: rendered hidden, measured, then moved
 * next to its trigger (`placed` turns true).
 */
@Injectable()
export class RagPreviewService {
  readonly entries = signal<CitationPreviewEntry[] | null>(null);
  readonly position = signal<PreviewPosition | null>(null);
  /** 0-based index of the source currently shown in the popover, if any. */
  readonly activeIndex = signal<number | null>(null);
  /** Element the popover is attached to (citation marker or eye button). */
  readonly anchor = signal<HTMLElement | null>(null);
  /** False until the popover has been measured and placed (avoids a flash). */
  readonly placed = signal(false);

  private popoverEl: HTMLElement | null = null;
  private align: PreviewAlign = 'start';
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private placeRaf: number | null = null;
  private listening = false;

  registerPopover(element: HTMLElement | null): void {
    this.popoverEl = element;
    if (element && this.entries()) this.schedulePlace();
  }

  /** True when the node belongs to the popover itself. */
  isPopover(node: EventTarget | Node | null): boolean {
    if (!this.popoverEl || !node) return false;
    return node === this.popoverEl || this.popoverEl.contains(node as Node);
  }

  /** Open the popover for a citation marker. No-op when already open on it. */
  show(
    marker: HTMLElement,
    sources: readonly AskSource[],
    indices?: number[],
    align: PreviewAlign = 'start',
  ): void {
    const resolved = (indices?.length ? indices : parseCitationIndices(marker.dataset['citationIndices']))
      .filter((n) => Number.isInteger(n) && n >= 0 && n < sources.length);

    if (!resolved.length || marker === this.anchor()) return;

    this.cancelHide();
    this.anchor()?.setAttribute('aria-expanded', 'false');
    this.anchor.set(marker);
    marker.setAttribute('aria-expanded', 'true');

    this.align = align;
    this.activeIndex.set(resolved[0] ?? null);
    this.entries.set(resolved.map((i) => ({ rank: i + 1, source: sources[i]! })));

    // Rendered hidden at the origin, measured, then moved next to the marker.
    this.placed.set(false);
    this.position.set({ top: 0, left: 0 });
    this.listen();
    this.schedulePlace();
  }

  /** Open the extract popover for one source, or close it if already shown on that control. */
  toggle(marker: HTMLElement, sources: readonly AskSource[], index: number): void {
    if (marker === this.anchor()) {
      this.hide();
      return;
    }
    this.show(marker, sources, [index], 'end');
  }

  isActiveMarker(node: EventTarget | Node | null): boolean {
    const anchor = this.anchor();
    return !!anchor && !!node && anchor.contains(node as Node);
  }

  hasActiveMarker(): boolean {
    return this.anchor() !== null;
  }

  scheduleHide(delay = 160): void {
    this.cancelHide();
    this.hideTimer = setTimeout(() => this.hide(), delay);
  }

  cancelHide(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  hide(): void {
    this.cancelHide();
    this.cancelPlace();
    this.unlisten();
    this.anchor()?.setAttribute('aria-expanded', 'false');
    this.anchor.set(null);
    this.activeIndex.set(null);
    this.entries.set(null);
    this.position.set(null);
    this.placed.set(false);
  }

  /**
   * Follow the trigger after the thread scrolls or the window resizes. Closes
   * the popover when the trigger left the DOM (virtual list) or the thread.
   */
  reposition(): void {
    const anchor = this.anchor();
    if (!anchor || !this.entries()) return;
    if (!anchor.isConnected || !isVisibleInThread(anchor)) {
      this.hide();
      return;
    }
    this.schedulePlace();
  }

  // ── Placement ──────────────────────────────────────────────────────────────

  private schedulePlace(): void {
    if (this.placeRaf !== null) return;
    if (typeof requestAnimationFrame !== 'function') {
      this.place();
      return;
    }
    this.placeRaf = requestAnimationFrame(() => {
      this.placeRaf = null;
      this.place();
    });
  }

  private cancelPlace(): void {
    if (this.placeRaf !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.placeRaf);
    }
    this.placeRaf = null;
  }

  /** Position the popover inside the widget panel (and the viewport). */
  private place(): void {
    const popover = this.popoverEl;
    const anchor = this.anchor();
    if (!popover || !anchor || !this.entries()) return;
    if (!anchor.isConnected) {
      this.hide();
      return;
    }

    const a = anchor.getBoundingClientRect();
    const b = panelBounds(anchor);
    popover.style.maxHeight = '';
    const width = popover.offsetWidth;
    let height = popover.offsetHeight;

    // Below the trigger, else above; when neither side fits, the roomier side
    // with the height capped to it (the content scrolls), so the popover
    // never hides the row or citation it belongs to.
    const spaceBelow = b.bottom - (a.bottom + GAP);
    const spaceAbove = a.top - GAP - b.top;
    let below = true;
    if (height > spaceBelow) {
      if (height <= spaceAbove) {
        below = false;
      } else {
        below = spaceBelow >= spaceAbove;
        const room = Math.max(spaceBelow, spaceAbove);
        if (room >= MIN_HEIGHT) {
          popover.style.maxHeight = `${Math.floor(room)}px`;
          height = popover.offsetHeight;
        }
      }
    }

    let left = this.align === 'end' ? a.right - width : a.left;
    let top = below ? a.bottom + GAP : a.top - GAP - height;

    left = clamp(left, b.left, b.right - width);
    top = clamp(top, b.top, b.bottom - height);
    this.position.set({ top: Math.round(top), left: Math.round(left) });
    this.placed.set(true);
  }

  // ── Dismissal ──────────────────────────────────────────────────────────────

  /** A click or tap outside the popover and its trigger closes it. */
  private readonly onPointerDown = (event: Event): void => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const anchor = this.anchor();
    if ((this.popoverEl && path.includes(this.popoverEl)) || (anchor && path.includes(anchor))) return;
    this.hide();
  };

  private listen(): void {
    if (this.listening || typeof document === 'undefined') return;
    this.listening = true;
    document.addEventListener('pointerdown', this.onPointerDown, true);
  }

  private unlisten(): void {
    if (!this.listening) return;
    this.listening = false;
    document.removeEventListener('pointerdown', this.onPointerDown, true);
  }
}

function parseCitationIndices(raw: string | undefined): number[] {
  return (raw ?? '')
    .split(',')
    .map((n) => Number.parseInt(n, 10))
    .filter((n) => Number.isInteger(n));
}

/** The widget panel's box, intersected with the viewport, inset by MARGIN. */
function panelBounds(anchor: HTMLElement): { left: number; top: number; right: number; bottom: number } {
  const vw = document.documentElement.clientWidth || window.innerWidth;
  const vh = window.innerHeight;
  const panel = anchor.closest('.rag-panel')?.getBoundingClientRect();
  return {
    left: Math.max(panel?.left ?? 0, 0) + MARGIN,
    top: Math.max(panel?.top ?? 0, 0) + MARGIN,
    right: Math.min(panel?.right ?? vw, vw) - MARGIN,
    bottom: Math.min(panel?.bottom ?? vh, vh) - MARGIN,
  };
}

function isVisibleInThread(anchor: HTMLElement): boolean {
  const a = anchor.getBoundingClientRect();
  if (a.width === 0 && a.height === 0) return false;
  const thread = anchor.closest('.rag-thread');
  if (!thread) return true;
  const t = thread.getBoundingClientRect();
  return a.bottom > t.top && a.top < t.bottom;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}
